import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button, LinkButton } from '../../components/ui/Button';
import { LoadingPanel } from '../../components/ui/Loader';
import { api } from '../../lib/api';
import { useSession } from '../../stores/session';
import { AuthLayout } from './AuthLayout';
import { useDocumentTitle } from '../../lib/title';

type State = 'working' | 'verified' | 'failed' | 'no-token';

export function VerifyEmailPage() {
  useDocumentTitle('Verify your email');

  const [params, setParams] = useSearchParams();
  const refreshUser = useSession((s) => s.refreshUser);
  const user = useSession((s) => s.user);

  const token = params.get('token');
  const [state, setState] = useState<State>(token ? 'working' : 'no-token');

  /*
   * StrictMode mounts effects twice in development, and these tokens are
   * single-use - so without this guard the second run always fails and the page
   * reports a valid link as expired. A ref rather than state because it must
   * not trigger a re-render, and must survive one.
   */
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) {
      return;
    }

    attempted.current = true;

    void (async () => {
      try {
        await api.post('/auth/verify-email', { token }, { anonymous: true });

        /*
         * Drop the token from the address bar.
         *
         * It has been spent, but the URL is now in history and will be sent as
         * a `Referer` on any outbound link from this page. Replacing rather than
         * pushing so the back button does not return to a dead link.
         */
        setParams({}, { replace: true });

        // If they happen to be signed in, `email_verified` just changed and the
        // UI should reflect it without a reload.
        await refreshUser();

        setState('verified');
      } catch {
        setState('failed');
      }
    })();
  }, [token, setParams, refreshUser]);

  if (state === 'working') {
    return (
      <AuthLayout title="Confirming your email">
        <LoadingPanel label="One moment" />
      </AuthLayout>
    );
  }

  if (state === 'verified') {
    return (
      <AuthLayout
        title="Email confirmed"
        subtitle="Your account is ready."
        footer={user ? undefined : <Link to="/login" className="text-accent hover:underline">Sign in</Link>}
      >
        <Alert tone="success">Thanks - you can upload tracks now.</Alert>
      </AuthLayout>
    );
  }

  if (state === 'no-token') {
    return <ResendPanel />;
  }

  return (
    <AuthLayout title="That link did not work">
      <div className="flex flex-col gap-4">
        {/*
          One message for expired, unknown, and already-used, matching the
          server. The already-used case is common and innocent: corporate link
          scanners and some mail clients fetch URLs before a human clicks.
        */}
        <Alert tone="error">
          This confirmation link is no longer valid. It may have expired, or already been used.
        </Alert>
        <ResendButton />
      </div>
    </AuthLayout>
  );
}

/** Shown when someone lands here with no token - usually from a nudge in the UI. */
function ResendPanel() {
  const user = useSession((s) => s.user);

  if (!user) {
    return (
      <AuthLayout
        title="Confirm your email"
        subtitle="Open the link we sent you."
        footer={<Link to="/login" className="text-accent hover:underline">Back to sign in</Link>}
      >
        <Alert tone="info">Sign in first if you need us to send the link again.</Alert>
      </AuthLayout>
    );
  }

  if (user.email_verified) {
    return (
      <AuthLayout title="Already confirmed" footer={<Link to="/" className="text-accent hover:underline">Go home</Link>}>
        <Alert tone="success">Your email address is confirmed.</Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Confirm your email" subtitle={`We sent a link to ${user.email}.`}>
      <div className="flex flex-col gap-4">
        <Alert tone="info">You can look around, but uploading needs a confirmed address.</Alert>
        <ResendButton />
      </div>
    </AuthLayout>
  );
}

function ResendButton() {
  const user = useSession((s) => s.user);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user) {
    return (
      <LinkButton to="/login" full>
        Sign in to resend
      </LinkButton>
    );
  }

  if (sent) {
    return <Alert tone="success">Sent. Check your inbox.</Alert>;
  }

  return (
    <Button
      full
      loading={busy}
      onClick={async () => {
        setBusy(true);

        try {
          await api.post('/auth/resend-verification');
        } catch {
          /*
           * Reported as sent regardless.
           *
           * The server answers 202 whether it sent or the throttle swallowed
           * it, so the only failures reaching here are transport-level. Telling
           * somebody "that failed" when the likely truth is "you already asked a
           * minute ago" would prompt them to keep clicking.
           */
        } finally {
          setBusy(false);
          setSent(true);
        }
      }}
    >
      Send it again
    </Button>
  );
}
